import React, { useState, useRef, useEffect } from "react";
import { Trash2 } from "lucide-react";
import QueueItem from "./QueueItem";

interface SwipeableQueueItemProps {
  title?: string;
  duration?: string;
  artist?: string;
  imageUrl?: string;
  listens?: number;
  likes?: number;
  isActive?: boolean;
  isBookmarked?: boolean;
  onClick?: () => void;
  onPodcastClick?: () => void;
  onBookmark?: () => void;
  onDelete?: () => void;
}

const SwipeableQueueItem = ({
  title,
  duration,
  artist,
  imageUrl,
  listens,
  likes,
  isActive,
  isBookmarked,
  onClick,
  onPodcastClick,
  onBookmark,
  onDelete,
}: SwipeableQueueItemProps) => {
  const [offset, setOffset] = useState(0);
  const touchStart = useRef(0);
  const isTouchDevice = useRef(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteThreshold = -80;
  const deleteActionThreshold = -120;

  useEffect(() => {
    isTouchDevice.current = "ontouchstart" in window;
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isTouchDevice.current) return;
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isTouchDevice.current) return;
    const currentTouch = e.touches[0].clientX;
    const diff = currentTouch - touchStart.current;
    const newOffset = Math.min(0, Math.max(-150, diff));
    setOffset(newOffset);
  };

  const handleTouchEnd = () => {
    if (!isTouchDevice.current) return;
    if (offset < deleteActionThreshold) {
      handleDelete();
    } else {
      setOffset(0);
    }
  };

  const handleDelete = () => {
    setIsDeleting(true);
    // Animate the item off-screen
    setOffset(-window.innerWidth);
    // Wait for animation to complete before calling onDelete
    setTimeout(() => {
      onDelete?.();
    }, 300); // Match this with the transition duration in CSS
  };

  // Calculate the opacity of the delete background based on swipe progress
  const getDeleteOpacity = () => {
    if (offset >= 0) return 0;
    return Math.min(1, Math.abs(offset / deleteThreshold));
  };

  return (
    <div className="relative overflow-hidden touch-pan-y">
      {/* Delete background */}
      <div
        className="absolute inset-0 bg-red-500 flex items-center justify-end"
        style={{
          opacity: getDeleteOpacity(),
        }}
      >
        <div className="px-6">
          <Trash2 className="h-6 w-6 text-white" />
        </div>
      </div>

      {/* Main content */}
      <div
        className={`relative z-10 transition-all duration-300 ease-out ${isDeleting ? "opacity-0" : "opacity-100"}`}
        style={{ transform: `translateX(${offset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <QueueItem
          title={title}
          duration={duration}
          imageUrl={imageUrl}
          listens={listens}
          likes={likes}
          isActive={isActive}
          isBookmarked={isBookmarked}
          onClick={onClick}
          onPodcastClick={onPodcastClick}
        />
      </div>
    </div>
  );
};

export default SwipeableQueueItem;
